<script setup>
import {computed, onMounted, onUnmounted, ref} from "vue";
defineOptions({ name: 'BulkSendView' })
import {useBulkSendStore} from "../../store/bulkSends.js";
import {useRoute} from "vue-router";
import EmptyState from "../../components/global/EmptyState.vue";
import Pagination from "../../components/section/Pagination.vue";
import SectionHeader from "../../components/section/SectionHeader.vue";

const store = useBulkSendStore()
const route = useRoute()
const loading = ref(true)
const page = ref(1)

let poller

const bulkSend = computed(() => store.bulk_send)
const recipients = computed(() => store.recipients)

const isSending = computed(() => ['queued', 'sending'].includes(bulkSend.value?.status))

const filterSummary = computed(() => {
    const filters = bulkSend.value?.filters ?? {}
    const parts = []

    if (filters.status) parts.push(filters.status.replace(/-/g, ' '))
    if (filters.start_date && filters.end_date) parts.push(`status dated ${filters.start_date} to ${filters.end_date}`)
    if (filters.excluded_order_uuids?.length) parts.push(`${filters.excluded_order_uuids.length} removed by hand`)

    return parts.join(' · ')
})

const getBulkSend = async (nextPage = page.value) => {
    page.value = nextPage
    await store.getBulkSend(route.params.uuid, {page: nextPage, limit: 50})
}

onMounted(async () => {
    await getBulkSend(1)
    loading.value = false

    // The completion notification is the primary signal; this just keeps an open
    // detail page current while a send is still working through its recipients.
    poller = setInterval(async () => {
        if (!isSending.value) return

        await getBulkSend()
    }, 5000)
})

onUnmounted(() => clearInterval(poller))
</script>

<template>
    <main v-if="loading">
        <empty-state icon="speechbubble-double" heading="Loading bulk send." content="Give it a sec, it's coming in a mo." />
    </main>

    <main v-else-if="bulkSend">
        <section>
            <section-header :heading="bulkSend.auto_reply?.description || bulkSend.auto_reply?.name || 'Bulk send.'" />

            <div class="bulk-send-detail-stats">
                <div class="bulk-send-detail-stat">
                    <p class="bulk-send-detail-stat-amount">{{ bulkSend.recipient_count }}</p>
                    <p class="bulk-send-detail-stat-label">recipients</p>
                </div>
                <div class="bulk-send-detail-stat">
                    <p class="bulk-send-detail-stat-amount">{{ bulkSend.sent_count }}</p>
                    <p class="bulk-send-detail-stat-label">sent</p>
                </div>
                <div class="bulk-send-detail-stat">
                    <p class="bulk-send-detail-stat-amount">{{ bulkSend.failed_count }}</p>
                    <p class="bulk-send-detail-stat-label">failed</p>
                </div>
                <div class="bulk-send-detail-stat">
                    <p class="bulk-send-detail-stat-amount">{{ bulkSend.skipped_count }}</p>
                    <p class="bulk-send-detail-stat-label">skipped</p>
                </div>
            </div>

            <p class="bulk-send-detail-filters" v-if="filterSummary">Filters: {{ filterSummary }}</p>
            <p class="bulk-send-detail-sending" v-if="isSending">Still sending — this page refreshes itself.</p>

            <div class="section-table" v-if="recipients?.data?.length">
                <div class="bulk-send-detail-row" v-for="r in recipients.data" :key="r.id">
                    <div class="bulk-send-detail-row-client">
                        <p class="bulk-send-detail-row-name">{{ r.name || 'Unknown' }}</p>
                        <p class="bulk-send-detail-row-meta">{{ r.phone_number }}</p>
                    </div>

                    <p class="bulk-send-detail-row-error" v-if="r.skip_reason">{{ r.skip_reason.replace(/_/g, ' ') }}</p>
                    <p class="bulk-send-detail-row-error" v-else-if="r.errors?.length">{{ r.errors[0].message }}</p>

                    <div :class="['bulk-send-detail-row-status', `--${r.status}`]">{{ r.status }}</div>
                </div>
            </div>

            <div class="section-footer" v-if="recipients?.data?.length">
                <pagination :pagination="recipients" @change-page="getBulkSend" />
            </div>
        </section>
    </main>

    <main v-else>
        <empty-state icon="speechbubble-double" heading="Bulk send not found." content="It may have been removed." />
    </main>
</template>

<style lang="scss">
.bulk-send-detail-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem;
    margin-top: 2rem;
    padding: 0 2rem;

    .bulk-send-detail-stat {
        background: var(--color-white);
        border-radius: 1.6rem;
        padding: 2rem;

        .bulk-send-detail-stat-amount {
            @include font(2.4, 2.4, book);
        }

        .bulk-send-detail-stat-label {
            @include font(1.2, 1.8, black);
            color: var(--color-grey-hazy);
        }

        @include dark() {
            background: #192D39;

            .bulk-send-detail-stat-amount { color: var(--color-white); }
        }
    }

    @include mobile() {
        grid-template-columns: repeat(2, 1fr);
    }
}

.bulk-send-detail-filters,
.bulk-send-detail-sending {
    padding: 1.5rem 2rem 0;
    @include font(1.3, 1.8, book);
    color: var(--color-grey-hazy);
    text-transform: capitalize;
}

.bulk-send-detail-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    padding: 1.5rem 2rem;
    background: var(--color-white);
    border-radius: 1.6rem;

    .bulk-send-detail-row-name {
        @include font(1.4, 2, black);
    }

    .bulk-send-detail-row-meta,
    .bulk-send-detail-row-error {
        @include font(1.2, 1.8, book);
        color: var(--color-grey-hazy);
    }

    .bulk-send-detail-row-error {
        color: var(--color-secondary-orange);
        text-transform: capitalize;
    }

    .bulk-send-detail-row-status {
        @include font(1.2, 1.2, black);
        padding: .8rem 1.6rem;
        border-radius: 8rem;
        background: var(--color-dew);
        text-transform: capitalize;

        &.--sent { background: var(--color-tiffany-dark); color: var(--color-white); }
        &.--failed { background: var(--color-secondary-orange); color: var(--color-white); }
    }

    @include dark() {
        background: #192D39;

        .bulk-send-detail-row-name { color: var(--color-white); }
    }
}
</style>
